import React, { useState, useCallback, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Shield, Award, Users, TrendingUp, Clock, FileCheck, Search, Building, ShoppingCart, Copyright, Star, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [contactFormOpen, setContactFormOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen">
      <Navigation onOpenContactForm={() => setContactFormOpen(true)} />
      <ContactFormDialog open={contactFormOpen} onOpenChange={setContactFormOpen} />

      <HeroSection onOpenContactForm={() => setContactFormOpen(true)} />
      <TrustSignalsSection />
      <AboutSection />
      <ServicesSection onOpenContactForm={() => setContactFormOpen(true)} />
      <WhyChooseUsSection />
      <ProcessSection />
      <PricingSection onOpenContactForm={() => setContactFormOpen(true)} />
      <IndustriesSection />
      <TeamSection />
      <TestimonialsSection />
      <CaseStudiesSection />
      <InsightsSection />
      <FaqsSection />
      <NewsletterSection />
      <CtaBannerSection onOpenContactForm={() => setContactFormOpen(true)} />
      <Footer />
    </div>
  );
};

const HeroSection = ({ onOpenContactForm }) => {
  return (
    <section 
      className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-cover bg-center"
      style={{ backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/345bb8f2-83fc-43aa-bc1a-7e6d1f8dce2e/images/afc1a0b16e58c295aa141cac150995d3ee9ae43bc6384dcc4053a825cbdbecb1.png)' }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-overlay"></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Protect Your Brand with Professional Trademark Registration Services
          </h1>
          <p className="text-lg lg:text-xl text-slate-200 leading-relaxed mb-8">
            Expert trademark registration, copyright protection, and intellectual property services in the USA. Our USPTO filing experts have successfully registered over 50,000 trademarks for businesses nationwide. From trademark search to federal trademark registration, we provide comprehensive trademark protection services tailored to your needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              onClick={onOpenContactForm}
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-10 py-6 text-lg font-semibold hover:-translate-y-1 transition-all"
              data-testid="hero-cta-button"
            >
              Register My Trademark
            </Button>
            <Button 
              variant="outline"
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white/20 px-10 py-6 text-lg font-semibold"
              onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-pricing-button"
            >
              View Pricing
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustSignalsSection = () => {
  const stats = [
    { value: '50,000+', label: 'Trademarks Filed', icon: FileCheck },
    { value: '98%', label: 'Success Rate', icon: Award },
    { value: '15+', label: 'Years Experience', icon: Clock },
    { value: '24/7', label: 'Support Available', icon: Users },
  ];

  return (
    <section className="py-16 bg-slate-50 border-b border-slate-200" data-testid="trust-signals-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center" data-testid={`trust-stat-${index}`}>
              <stat.icon className="h-10 w-10 mx-auto mb-3 text-[#B4905A]" />
              <div className="text-3xl lg:text-4xl font-bold text-[#1E3A8A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stat.value}
              </div>
              <div className="text-slate-600 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-20 lg:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <img 
              src="https://images.unsplash.com/photo-1769739576456-0aefcff3f4b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBvZmZpY2UlMjB0ZWFtd29ya3xlbnwwfHx8fDE3NzUyNDA5OTV8MA&ixlib=rb-4.1.0&q=85"
              alt="Professional team meeting"
              className="w-full h-[500px] object-cover border border-slate-200 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
            />
          </div>
          <div>
            <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              About Trademark Company: Your Trusted Intellectual Property Partner
            </h2>
            <div className="space-y-4 text-base leading-relaxed text-slate-600">
              <p>
                At Trademark Company, we specialize in providing comprehensive trademark registration services and intellectual property protection for businesses across the United States. With over 15 years of experience as USPTO filing experts, we have successfully helped more than 50,000 clients protect their brand names, logos, and intellectual assets through federal trademark registration.
              </p>
              <p>
                Our mission is to make trademark protection accessible and affordable for businesses of all sizes. Whether you're a startup looking to register your first trademark, an established company expanding your brand portfolio, or an e-commerce seller needing Amazon Brand Registry assistance, our team of trademark lawyers and specialists provides expert guidance every step of the way.
              </p>
              <p>
                We understand that your brand is your most valuable asset. That's why we offer end-to-end trademark services including comprehensive trademark search, federal filing, office action responses, and ongoing trademark monitoring. Our streamlined online trademark registration process, combined with personalized support from dedicated case managers, ensures your intellectual property receives the protection it deserves.
              </p>
              <p>
                Beyond trademark registration, we offer copyright registration services, company formation assistance, and strategic IP consulting to help you build a robust intellectual property portfolio. Our commitment to excellence, transparent pricing, and 98% success rate make us the preferred choice for trademark services nationwide.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-[#1E3A8A]" />
                <span className="font-semibold text-slate-900">USPTO Registered</span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="h-5 w-5 text-[#1E3A8A]" />
                <span className="font-semibold text-slate-900">Award-Winning Service</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mobile Carousel Wrapper
const MobileCarousel = ({ children, itemCount }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <>
      <div className="lg:hidden">
        <div className="relative">
          <div className="embla overflow-hidden" ref={emblaRef}>
            <div className="embla__container">
              {React.Children.map(children, (child, index) => (
                <div key={index} className="embla__slide">
                  {child}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-center gap-4 mt-6">
            <Button variant="outline" size="icon" onClick={scrollPrev} data-testid="carousel-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={scrollNext} data-testid="carousel-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="hidden lg:grid lg:grid-cols-3 gap-6 lg:gap-8">
        {children}
      </div>
    </>
  );
};

const ServicesSection = ({ onOpenContactForm }) => {
  const services = [
    {
      icon: FileCheck,
      title: 'Trademark Registration Services',
      description: 'Comprehensive federal trademark registration services to protect your brand name and logo. Our trademark attorneys handle the entire filing process, from initial trademark search to USPTO submission and office action responses. We ensure your trademark application meets all legal requirements for successful registration.',
      link: '/trademark-registration',
    },
    {
      icon: Copyright,
      title: 'Copyright Registration Services',
      description: 'Protect your creative works with our streamlined copyright registration services. We handle registration for literary works, artistic creations, software, and multimedia content. Our experts ensure your intellectual property receives comprehensive legal protection through proper US copyright registration.',
      link: '/copyright-registration',
    },
    {
      icon: Building,
      title: 'Company Formation',
      description: 'Start your business right with our company formation services. We assist with LLC formation, corporation setup, and all necessary business registrations. Our team handles state filings, EIN applications, and ensures your business structure is optimized for trademark protection and growth.',
      link: '/company-formation',
    },
    {
      icon: Search,
      title: 'Trademark Search Services',
      description: 'Before filing, conduct a comprehensive trademark search to avoid conflicts. Our trademark name search covers federal databases, state registrations, and common law uses. We provide detailed reports with refusal risk assessment, ensuring your trademark application has the best chance of approval.',
      link: '/trademark-search',
    },
    {
      icon: ShoppingCart,
      title: 'Amazon Brand Registry',
      description: 'Protect your products on Amazon with Brand Registry enrollment. We help e-commerce sellers register trademarks and navigate the Amazon Brand Registry process. Gain enhanced brand protection, access to A+ Content, and powerful tools to prevent counterfeiters from hijacking your listings.',
      link: '/amazon-brand-registry',
    },
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Comprehensive Trademark & Intellectual Property Services
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            From trademark registration to copyright protection, we offer complete intellectual property solutions tailored to protect and grow your business. Our expert team provides personalized guidance for every service we offer.
          </p>
        </div>

        <MobileCarousel itemCount={services.length + 1}>
          {services.map((service, index) => (
            <Link 
              key={index}
              to={service.link}
              className="block bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1"
              data-testid={`service-card-${index}`}
            >
              <service.icon className="h-12 w-12 text-[#1E3A8A] mb-4" />
              <h3 className="text-xl lg:text-2xl font-medium tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {service.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600 mb-4">
                {service.description}
              </p>
              <div className="flex items-center text-[#1E3A8A] font-semibold">
                Learn More <ArrowRight className="ml-2 h-4 w-4" />
              </div>
            </Link>
          ))}
          <div
            className="bg-[#1E3A8A] text-white border border-[#1E40AF] p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all hover:-translate-y-1 flex flex-col justify-center items-center text-center cursor-pointer"
            onClick={onOpenContactForm}
            data-testid="service-cta-card"
          >
            <FileCheck className="h-16 w-16 mb-4 text-[#B4905A]" />
            <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Protect Your Brand?
            </h3>
            <p className="text-slate-200 mb-6 leading-relaxed">
              Start your trademark registration today and secure your intellectual property rights.
            </p>
            <Button 
              className="bg-[#B4905A] hover:bg-[#927448] text-white px-8 py-4 font-semibold"
              data-testid="services-cta-button"
            >
              Register My Trademark
            </Button>
          </div>
        </MobileCarousel>
      </div>
    </section>
  );
};

const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: Shield,
      title: 'USPTO Registered Experts',
      description: 'Our trademark attorneys are registered with the USPTO and have extensive experience in federal trademark law and intellectual property protection.',
    },
    {
      icon: Award,
      title: '98% Approval Success Rate',
      description: 'Our comprehensive trademark search and expert filing process ensures the highest success rate in the industry for trademark registration.',
    },
    {
      icon: TrendingUp,
      title: 'Fast Turnaround Time',
      description: 'We prioritize your trademark filing with expedited processing options and same-day application review to get your trademark protected quickly.',
    },
    {
      icon: Users,
      title: 'Dedicated Case Manager',
      description: 'Every client receives a dedicated trademark specialist who guides you through the entire registration process from start to finish.',
    },
    {
      icon: Check,
      title: 'Comprehensive Trademark Search',
      description: 'Before filing, we conduct thorough searches across federal, state, and common law databases to identify potential conflicts.',
    },
    {
      icon: Clock,
      title: 'Lifetime Support & Monitoring',
      description: 'Ongoing trademark monitoring alerts you to potential infringements, plus lifetime access to our support team for renewals and questions.',
    },
  ];

  return (
    <section className="py-20 lg:py-32" data-testid="why-choose-us-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Why Choose Trademark Company for Your IP Protection
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            With over 15 years of experience and 50,000+ successful trademark registrations, we are the trusted choice for businesses seeking professional trademark services and intellectual property protection.
          </p>
        </div>

        <MobileCarousel itemCount={reasons.length}>
          {reasons.map((reason, index) => (
            <div 
              key={index}
              className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all"
              data-testid={`why-choose-card-${index}`}
            >
              <reason.icon className="h-12 w-12 text-[#1E3A8A] mb-4" />
              <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {reason.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600">
                {reason.description}
              </p>
            </div>
          ))}
        </MobileCarousel>
      </div>
    </section>
  );
};

const ProcessSection = () => {
  const steps = [
    {
      number: '01',
      title: 'Trademark Search & Consultation',
      description: 'We begin with a comprehensive trademark search across federal and state databases to ensure your desired trademark is available. Our experts review your case and provide a detailed risk assessment with recommendations for successful registration.',
    },
    {
      number: '02',
      title: 'Application Preparation & Filing',
      description: 'Our trademark attorneys prepare your application with precise legal descriptions, proper classifications, and supporting documentation. We file directly with the USPTO and provide you with immediate confirmation and tracking information.',
    },
    {
      number: '03',
      title: 'Monitoring & Final Registration',
      description: 'We monitor your application throughout the examination process, handle any office actions or objections, and manage the publication period. Once approved, you receive your official trademark registration certificate with ongoing monitoring services.',
    },
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our Simple 3-Step Trademark Registration Process
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            We've streamlined the trademark registration process to make protecting your brand simple, transparent, and stress-free. From initial search to final registration, we handle everything.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div 
              key={index}
              className="process-step bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
              data-testid={`process-step-${index}`}
            >
              <div className="text-5xl font-bold text-[#B4905A] mb-4 opacity-50" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step.number}
              </div>
              <h3 className="text-xl lg:text-2xl font-medium tracking-tight text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {step.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PricingSection = ({ onOpenContactForm }) => {
  const plans = [
    {
      name: 'Essentials',
      price: '$45',
      fee: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured',
      ],
      note: 'Govt. filing fee not included',
    },
    {
      name: 'Enhanced',
      price: '$149',
      fee: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
      ],
      note: 'Govt. filing fee not included',
    },
    {
      name: 'Deluxe',
      price: '$299',
      fee: '+ Federal Filing Fee',
      popular: true,
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
      ],
      note: 'Govt. filing fee not included',
    },
    {
      name: 'Platinum',
      price: '$349',
      fee: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Search Report',
        'High Priority Service',
        'Cease & Desist Letter',
      ],
      note: 'Govt. filing fee not included',
    },
  ];

  return (
    <section id="pricing" className="py-20 lg:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Transparent Trademark Registration Pricing
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Choose the trademark registration package that best fits your needs. All plans include expert guidance and USPTO filing. No hidden fees, just transparent pricing.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, index) => (
            <div 
              key={index}
              className={`bg-white border-2 p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)] flex flex-col ${
                plan.popular ? 'border-[#B4905A]' : 'border-slate-200'
              }`}
              data-testid={`pricing-card-${index}`}
            >
              {plan.popular && (
                <div className="bg-[#B4905A] text-white text-xs font-bold py-1 px-3 -mt-6 -mx-6 mb-4 text-center">
                  RECOMMENDED
                </div>
              )}
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {plan.name}
              </h3>
              <div className="mb-4">
                <span className="text-4xl font-bold text-[#1E3A8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {plan.price}
                </span>
                <span className="text-sm text-slate-600 block">{plan.fee}</span>
              </div>
              <div className="flex-1 mb-6 max-h-[280px] overflow-y-auto pr-2">
                <ul className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Check className="h-4 w-4 text-[#1E3A8A] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-xs text-slate-500 italic mb-4">{plan.note}</p>
              <Button 
                onClick={onOpenContactForm}
                className={`w-full py-6 font-semibold ${
                  plan.popular 
                    ? 'bg-[#B4905A] hover:bg-[#927448] text-white' 
                    : 'bg-[#1E3A8A] hover:bg-[#1E40AF] text-white'
                }`}
                data-testid={`pricing-cta-${index}`}
              >
                Get Started
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const IndustriesSection = () => {
  const industries = [
    { name: 'Technology & Software', description: 'Protect your tech innovations, software brands, and digital products with comprehensive trademark coverage.' },
    { name: 'E-Commerce & Retail', description: 'Secure your online store brand, product lines, and Amazon seller accounts with trademark registration.' },
    { name: 'Food & Beverage', description: 'Register trademarks for restaurant names, food products, beverage brands, and culinary concepts.' },
    { name: 'Healthcare & Wellness', description: 'Trademark protection for medical practices, health products, wellness brands, and pharmaceutical companies.' },
    { name: 'Fashion & Apparel', description: 'Protect clothing brands, fashion labels, accessory lines, and design elements with federal trademarks.' },
    { name: 'Professional Services', description: 'Trademark registration for law firms, consulting companies, agencies, and service-based businesses.' },
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="industries-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Industries We Serve Across America
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Our trademark registration services support businesses across all industries. From startups to Fortune 500 companies, we provide tailored intellectual property solutions.
          </p>
        </div>

        <MobileCarousel itemCount={industries.length}>
          {industries.map((industry, index) => (
            <div 
              key={index}
              className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
              data-testid={`industry-card-${index}`}
            >
              <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {industry.name}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600">
                {industry.description}
              </p>
            </div>
          ))}
        </MobileCarousel>
      </div>
    </section>
  );
};

const TeamSection = () => {
  return (
    <section className="py-20 lg:py-32" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Our Expert Team of Trademark Attorneys
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Our team consists of experienced trademark lawyers, USPTO-registered attorneys, and intellectual property specialists dedicated to protecting your brand. With decades of combined experience, we have the expertise to handle even the most complex trademark matters.
          </p>
        </div>
        <div className="bg-white border border-slate-200 p-12 text-center shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
          <img 
            src="https://images.unsplash.com/photo-1758518731706-be5d5230e5a5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBtZWV0aW5nfGVufDB8fHx8MTc3NTIzOTAyNXww&ixlib=rb-4.1.0&q=85"
            alt="Our team"
            className="w-full h-[400px] object-cover mb-8"
          />
          <p className="text-base leading-relaxed text-slate-600 max-w-3xl mx-auto">
            Every member of our team is committed to providing exceptional service and personalized attention. From your initial consultation through final registration, you'll work with dedicated professionals who understand your business needs and intellectual property goals.
          </p>
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Jennifer Williams',
      company: 'Tech Startup Founder',
      text: 'Trademark Company made the entire trademark registration process seamless. Their team was knowledgeable, responsive, and guided us through every step. Our trademark was approved in record time!',
      rating: 5,
    },
    {
      name: 'Robert Chen',
      company: 'E-Commerce Business Owner',
      text: 'I needed help with Amazon Brand Registry and trademark filing. The team at Trademark Company handled everything professionally and got my brand registered quickly. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Maria Rodriguez',
      company: 'Restaurant Chain Owner',
      text: 'Protecting our restaurant brand was crucial before expansion. Trademark Company provided comprehensive trademark search services and filed our application with expertise. Worth every penny!',
      rating: 5,
    },
    {
      name: 'David Thompson',
      company: 'Software Development CEO',
      text: 'Outstanding service from start to finish. They handled our trademark application efficiently and kept us informed throughout the entire process. Our brand is now federally protected!',
      rating: 5,
    },
    {
      name: 'Amanda Foster',
      company: 'Fashion Designer',
      text: 'I was worried about trademark conflicts, but their comprehensive search gave me peace of mind. The attorneys were incredibly helpful and my trademark registration was approved without any issues.',
      rating: 5,
    },
    {
      name: 'James Patterson',
      company: 'Marketing Agency Owner',
      text: 'Professional, efficient, and affordable trademark services. They made a complex process simple and manageable. I\'ve already recommended them to several business colleagues.',
      rating: 5,
    },
    {
      name: 'Lisa Nguyen',
      company: 'Health & Wellness Brand',
      text: 'The dedicated case manager assigned to us was fantastic. They answered all our questions promptly and handled our trademark filing with great attention to detail. Very satisfied!',
      rating: 5,
    },
    {
      name: 'Christopher Martinez',
      company: 'Real Estate Developer',
      text: 'Exceptional trademark attorneys who truly understand intellectual property law. They protected our brand name and logo perfectly. I trust them completely with all our IP needs.',
      rating: 5,
    },
    {
      name: 'Rachel Kim',
      company: 'Beauty Products Entrepreneur',
      text: 'Fast, reliable, and expert trademark registration services. They got my Amazon Brand Registry approved quickly, which has been a game-changer for protecting my products online.',
      rating: 5,
    },
  ];

  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    align: 'start',
    containScroll: 'trimSnaps',
    loop: true,
  });

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            What Our Clients Say About Our Trademark Services
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Don't just take our word for it. Here's what business owners across the country say about our trademark registration services.
          </p>
        </div>

        {/* Carousel for All Testimonials */}
        <div className="relative">
          <div className="embla overflow-hidden" ref={emblaRef}>
            <div className="embla__container">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="embla__slide">
                  <div 
                    className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
                    data-testid={`testimonial-card-${index}`}
                  >
                    <div className="flex mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="h-5 w-5 fill-[#B4905A] text-[#B4905A]" />
                      ))}
                    </div>
                    <p className="text-sm leading-relaxed text-slate-700 mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-600">{testimonial.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Carousel Navigation Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={scrollPrev}
              className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white"
              data-testid="testimonial-carousel-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={scrollNext}
              className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white"
              data-testid="testimonial-carousel-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

const CaseStudiesSection = () => {
  return (
    <section className="py-20 lg:py-32" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Success Stories: Trademarks We've Secured
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Over 15 years, we've helped thousands of businesses successfully register their trademarks and protect their intellectual property. Here are just a few success stories.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]" data-testid="case-study-1">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tech Startup Protects Innovative Software Brand
            </h3>
            <p className="text-sm leading-relaxed text-slate-600 mb-4">
              A Silicon Valley startup approached us needing urgent trademark protection before their product launch. We conducted a comprehensive trademark search, identified potential conflicts, and successfully registered their trademark in just 4 months. Today, their brand is valued at over $10 million.
            </p>
            <div className="text-sm font-semibold text-[#1E3A8A]">Industry: Technology | Timeline: 4 Months | Status: Registered</div>
          </div>

          <div className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)]" data-testid="case-study-2">
            <h3 className="text-xl font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Amazon Seller Achieves Brand Registry Status
            </h3>
            <p className="text-sm leading-relaxed text-slate-600 mb-4">
              An e-commerce entrepreneur was losing thousands to counterfeit sellers on Amazon. We registered their trademark and helped them enroll in Amazon Brand Registry. Within weeks, counterfeit listings were removed, and their sales increased by 300%.
            </p>
            <div className="text-sm font-semibold text-[#1E3A8A]">Industry: E-Commerce | Timeline: 3 Months | Status: Protected</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const InsightsSection = () => {
  const insights = [
    {
      title: 'Understanding USPTO Trademark Classes',
      excerpt: 'Learn how to choose the right trademark classification for your goods and services. Proper classification is crucial for comprehensive brand protection.',
    },
    {
      title: 'Common Trademark Registration Mistakes to Avoid',
      excerpt: 'Discover the top errors that lead to trademark application refusals and how to avoid them with proper preparation and expert guidance.',
    },
    {
      title: 'When to File for Trademark Protection',
      excerpt: 'Timing matters in trademark registration. Find out when you should file your trademark application to maximize protection and avoid conflicts.',
    },
  ];

  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="insights-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Intellectual Property Insights & Resources
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Stay informed with expert articles, guides, and resources about trademark registration, copyright protection, and intellectual property law.
          </p>
        </div>

        <MobileCarousel itemCount={insights.length}>
          {insights.map((insight, index) => (
            <div 
              key={index}
              className="bg-white border border-slate-200 p-8 shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all cursor-pointer"
              data-testid={`insight-card-${index}`}
            >
              <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {insight.title}
              </h3>
              <p className="text-sm leading-relaxed text-slate-600 mb-4">
                {insight.excerpt}
              </p>
              <div className="flex items-center text-[#1E3A8A] font-semibold text-sm">
                Read Article <ArrowRight className="ml-2 h-4 w-4" />
              </div>
            </div>
          ))}
        </MobileCarousel>
      </div>
    </section>
  );
};

const FaqsSection = () => {
  const faqs = [
    {
      question: 'What is a trademark and why do I need trademark registration?',
      answer: 'A trademark is a distinctive sign, symbol, word, or phrase that identifies and distinguishes your goods or services from competitors. Federal trademark registration with the USPTO provides nationwide protection, exclusive rights to use your mark, legal presumption of ownership, and the ability to prevent others from using confusingly similar marks. It\'s essential for building brand recognition and protecting your business investment.',
    },
    {
      question: 'How long does the trademark registration process take?',
      answer: 'The trademark registration process typically takes 8-12 months from filing to final registration, though timelines vary based on USPTO workload and whether any office actions are issued. Our expedited service can help move your application through the system faster. We provide regular updates throughout the process and handle any responses needed to the trademark office.',
    },
    {
      question: 'What is included in your trademark search services?',
      answer: 'Our comprehensive trademark search includes searching the USPTO federal database, state trademark registrations, common law uses, domain names, and business registrations. We provide a detailed report analyzing potential conflicts, assessing refusal risks, and recommending strategies for successful registration. This thorough search helps avoid costly rejections and legal disputes.',
    },
    {
      question: 'Can I trademark my business name and logo?',
      answer: 'Yes, you can trademark both your business name and logo as separate trademarks, or together as a combined mark. We recommend registering them separately for maximum protection - this allows you flexibility to use each element independently and provides broader coverage. Our trademark attorneys will advise on the best filing strategy for your specific situation.',
    },
    {
      question: 'What happens if my trademark application is refused?',
      answer: 'If the USPTO issues an office action or refusal, our experienced trademark attorneys will review the objection and prepare a comprehensive response. Many initial refusals can be overcome with proper legal arguments and evidence. We handle all office action responses as part of our service and work diligently to address examiner concerns and move your application toward approval.',
    },
    {
      question: 'Do you offer trademark monitoring services?',
      answer: 'Yes, we provide ongoing trademark monitoring services to protect your registered trademark. Our monitoring system alerts you to new trademark applications that may conflict with your mark, potential infringements, and unauthorized uses. We also remind you of renewal deadlines and maintenance requirements to keep your trademark registration active.',
    },
    {
      question: 'What\'s the difference between state and federal trademark registration?',
      answer: 'State trademark registration provides protection only within that specific state, while federal registration through the USPTO provides nationwide protection. Federal registration offers stronger legal rights, the ability to sue in federal court, use of the ® symbol, and protection for online trademark registration. We strongly recommend federal registration for businesses planning to expand or operate online.',
    },
    {
      question: 'Can you help with Amazon Brand Registry?',
      answer: 'Absolutely! We specialize in helping Amazon sellers register trademarks and enroll in Amazon Brand Registry. Having a registered trademark is required for Brand Registry, which provides enhanced content options, brand protection tools, and better control over your product listings. We handle the entire process from trademark filing to Brand Registry enrollment.',
    },
  ];

  return (
    <section id="faqs" className="py-20 lg:py-32" data-testid="faqs-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions About Trademark Registration
          </h2>
          <p className="text-base leading-relaxed text-slate-600">
            Get answers to common questions about trademark services, the registration process, and intellectual property protection.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="bg-white border border-slate-200 px-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
              data-testid={`faq-item-${index}`}
            >
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-[#1E3A8A]">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600 pt-2">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const NewsletterSection = () => {
  return (
    <section className="py-20 lg:py-32 bg-slate-50" data-testid="newsletter-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Stay Updated on Trademark & IP Law Changes
        </h2>
        <p className="text-base leading-relaxed text-slate-600 mb-8">
          Subscribe to our newsletter for expert insights on trademark registration, intellectual property protection, USPTO updates, and exclusive tips for protecting your brand. Join thousands of business owners who stay informed with our monthly IP newsletter.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
          <Input 
            type="email" 
            placeholder="Enter your email address" 
            className="flex-1"
            data-testid="newsletter-email"
          />
          <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white px-8 py-4 font-semibold" data-testid="newsletter-submit">
            Subscribe
          </Button>
        </div>
      </div>
    </section>
  );
};

const CtaBannerSection = ({ onOpenContactForm }) => {
  return (
    <section 
      className="relative py-20 lg:py-32 bg-cover bg-center" 
      style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758599543152-a73184816eba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMHN1Y2Nlc3N8ZW58MHx8fHwxNzc1MjQ2NTUzfDA&ixlib=rb-4.1.0&q=85)' }}
      data-testid="cta-banner-section"
    >
      <div className="absolute inset-0 bg-[#1E3A8A]/90"></div>
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
        <h2 className="text-3xl lg:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Ready to Secure Your Brand's Future?
        </h2>
        <p className="text-lg lg:text-xl text-slate-100 leading-relaxed mb-8">
          Join thousands of satisfied clients who have protected their brands with our expert trademark registration services. Don't risk losing your brand identity—take action today and ensure your intellectual property is fully protected.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            onClick={onOpenContactForm}
            className="bg-[#B4905A] hover:bg-[#927448] text-white px-10 py-6 text-lg font-semibold hover:-translate-y-1 transition-all"
            data-testid="cta-banner-button"
          >
            Start Your Registration Now
          </Button>
          <Button 
            variant="outline"
            className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1E3A8A] px-10 py-6 text-lg font-semibold transition-all"
            onClick={() => window.location.href = 'tel:+11231234567'}
            data-testid="cta-banner-call-button"
          >
            Call +1 (123) 123-4567
          </Button>
        </div>
        <p className="mt-6 text-sm text-slate-200">🎉 Special Offer: Free trademark search with any registration package!</p>
      </div>
    </section>
  );
};

export default HomePage;
